<template>
  <div class="fts-console" :class="{ mh: vectorId }">
    <!-- module card -->
    <div class="console-card console-card-module">
      <!-- header -->
      <div
        class="console-card-header"
        :class="{ 'tour-console-create-module': !vectorId }"
        ref="moduleCardHeader"
      >
        <div class="console-card-header-wrapper">
          <!-- name -->
          <div class="console-card-module-info">
            <!-- emoji -->
            <fts-emoji
              :appendToBody="true"
              :toggleSize="18"
              v-model="vector.emoji"
              class="console-card-module-emoji"
            />
            <!-- name -->
            <fts-contenteditable
              :value="vector.name"
              placeholder="Please enter module name"
              class="console-card-module-name"
              :key="vectorId"
              @input="updateModuleName"
              ref="moduleName"
            />
            <!-- profile -->
            <div class="console-card-module-tag" v-if="vector.isUserCollection">
              <font-awesome-icon slot="trigger" :icon="['fas', 'user']" fixed-width />
            </div>
          </div>
          <!-- error -->
          <div class="console-card-error" v-if="moduleNameError()">
            {{moduleNameError()}}
          </div>
          <!-- description -->
          <fts-contenteditable
            v-model="vector.description"
            placeholder="enter module description"
            class="console-card-module-description"
          />
          <!-- schema -->
            <div v-if="isDevelopment">
                  <fts-btn
                    sm
                    outline
                    text-transform-init
                    @click="openSchemaScriptModal"
                  >
                    Schema
                  </fts-btn>
            </div>
        </div>
        <!-- toggle -->
        <div class="console-card-toggle" v-if="vectorId">
          <!-- user collection -->
          <fts-toggle-switch
            :form-data="vector"
            :item="{ fieldName: 'isUserCollection' }"
            :size="14"
            color="primary"
            mat-class
            label-text="User Profile"
            label-dir="left"
          >
            <div
              slot="label"
              slot-scope="{label, defaultClass}"
              class="console-card-toggle-label"
              :class="defaultClass"
            >
              {{label}}
              <fts-tippy
                content="Connects this module to users. Able to manage user access or permission through profile."
              >
                <font-awesome-icon
                  slot="trigger"
                  :icon="['fas', 'info-circle']"
                  class="label-helper"
                />
              </fts-tippy>
            </div>
          </fts-toggle-switch>
          <!-- publish -->
          <fts-toggle-switch
            :form-data="vector"
            :item="{ fieldName: 'published' }"
            :size="14"
            color="primary"
            mat-class
            label-text="Publish"
            label-dir="left"
          >
            <div
              slot="label"
              slot-scope="{label, defaultClass}"
              class="console-card-toggle-label"
              :class="defaultClass"
            >
              {{label}}
              <fts-tippy content="Set module to publish or unpublish.">
                <font-awesome-icon
                  slot="trigger"
                  :icon="['fas', 'info-circle']"
                  class="label-helper"
                />
              </fts-tippy>
            </div>
          </fts-toggle-switch>
        </div>
      </div>
      <!-- body -->
      <div class="console-card-body">
        <!-- edit -->
        <template v-if="vectorId">
          <div class="console-card-content">
            <div>
              <!-- automation -->
              <div
                class="console-card-automation-container"
                v-if="
                  (getAutomation(card.name) &&
                  getAutomation(card.name).length) ||
                  scheduleTemplates.length
                "
              >
                <fts-event
                  v-for="event in getAutomation(card.name)"
                  :key="event.id"
                  :event="event"
                  @open="showAutomationDesignModal"
                />
                <!-- schedules (docs/scheduling-routine-automation-proposal.md §2): always listed,
                     so a converted one is visible even while the adder is off -->
                <fts-event
                  v-for="event in scheduleTemplates"
                  :key="event.id"
                  :event="event"
                  @open="showAutomationDesignModal"
                />
              </div>
              <!-- audit -->
              <div class="console-card-audit" v-if="vector.audit && vector.audit.updatedBy">
                <div class="console-card-audit-by">last updated by</div>
                <fts-profile :user-name="vector.audit.updatedBy" />
                <div
                  class="console-card-audit-date"
                >{{moment(vector.audit.updatedDate).format('lll')}}</div>
              </div>
            </div>
            <!-- actions -->
            <div class="console-card-content-actions">
              <div class="console-card-action-group">
                <!-- add notification -->
                <fts-tippy content="Add Notification">
                  <fts-btn icon text @click="addAutomation(card, 'notification')">
                    <font-awesome-icon :icon="['far', 'envelope']" />
                  </fts-btn>
                </fts-tippy>
                <!-- add automation -->
                <fts-tippy content="Add Automation">
                  <fts-btn icon text @click="addAutomation(card, 'automation')">
                    <font-awesome-icon :icon="['fas', 'bolt']" />
                  </fts-btn>
                </fts-tippy>
                <!-- add schedule: runs on a clock over the entries matching a condition -->
                <fts-tippy content="Add Schedule" v-if="studioScheduleEnabled">
                  <fts-btn icon text @click="addSchedule" data-add-schedule>
                    <font-awesome-icon :icon="['fas', 'clock']" />
                  </fts-btn>
                </fts-tippy>
              </div>
              <div class="console-card-action-group">
                <!-- authorization -->
                <fts-auth type="vector">
                  <fts-btn
                    slot="trigger"
                    slot-scope="{ show, isRight }"
                    icon
                    text
                    :color="isRight ? 'danger' : 'default'"
                    @click="show"
                  >
                    <font-awesome-icon :icon="['fas', 'lock']" v-if="isRight" />
                    <font-awesome-icon :icon="['fas', 'unlock']" v-else />
                  </fts-btn>
                </fts-auth>
              </div>
              <div class="console-card-action-group">
                <!-- automation and notification panel -->
                <fts-tippy content="Automation and Notification panel" v-if="beta">
                  <fts-btn icon text @click="openModuleAutomationPanel">
                    <font-awesome-icon :icon="['fas', 'play']" />
                  </fts-btn>
                </fts-tippy>
                <!-- notification status -->
                <fts-tippy content="Notification status">
                  <fts-btn
                    icon
                    text
                    :color="showNotificationPanel ? 'primary' : 'default'"
                    @click="toggleNotificationPanel"
                  >
                    <font-awesome-icon :icon="['far', 'bell']" />
                  </fts-btn>
                </fts-tippy>
                <!-- history -->
                <fts-tippy content="History">
                  <fts-btn icon text @click="openModuleHistory">
                    <font-awesome-icon :icon="['fas', 'clock-rotate-left']" />
                  </fts-btn>
                </fts-tippy>
              </div>
              <div class="console-card-action-group">
                <!-- delete -->
                <fts-tippy content="Delete module">
                  <fts-btn icon text @click="deleteModule">
                    <font-awesome-icon :icon="['far', 'trash-alt']" />
                  </fts-btn>
                </fts-tippy>
                <!-- setting -->
                <fts-tippy content="Settings">
                  <fts-btn
                    icon
                    text
                    :color="moduleAdvancedSettings ? 'primary' : 'default'"
                    @click="toggleModuleSettings"
                    v-if="beta"
                  >
                    <font-awesome-icon :icon="['fas', 'cog']" />
                  </fts-btn>
                </fts-tippy>
              </div>
            </div>
          </div>
          <!-- notification panel -->
          <fts-transition-expand>
            <div
              class="console-card-module-form"
              :class="{ 'form-divider': showNotificationPanel }"
              v-if="vectorId && showNotificationPanel"
            >
              <notification-panel />
            </div>
          </fts-transition-expand>
          <!-- guarded-change apply history (D12, alpha D14) -->
          <fts-canvas-apply-history v-if="vectorId" />
          <!-- settings -->
          <fts-transition-expand>
            <div
              class="fts-row gutters console-card-module-form comfort-form-design"
              :class="{ 'form-divider': moduleAdvancedSettings }"
              v-if="vectorId && moduleAdvancedSettings"
            >
              <div class="fts-col">
                <div class="fts-form-helper">
                  ** All settings listed here are in <fts-release-badge class="fts-align-text-bottom" /> release.
                </div>
              </div>
              <!-- owner workspace -->
              <div class="fts-col-6">
                <fts-form-element
                  :form-data="vector"
                  :item="{
                    fieldName: 'ownerWorkspaceId',
                    icon: 'fts-icon-menu-caret-down'
                  }"
                  comfort-design
                >
                  <fts-select
                    slot="formElement"
                    label="name"
                    :reduce="(x) => x"
                    :filterable="false"
                    :options="workspaceOptions"
                    v-model="moduleWorkspace"
                    comfort-design
                    @search="fetchWorkspaceOptions"
                    @search:focus="preloadWorkspaces"
                  />
                </fts-form-element>
              </div>
              <!-- version -->
              <div class="fts-col-6">
                <fts-form-element
                  :form-data="vector"
                  :item="{ fieldName: 'version', type: 0 }"
                  comfort-design
                />
              </div>
              <!-- workspaces -->
              <div class="fts-col">
                <fts-form-element
                  :form-data="vector"
                  :item="{
                    fieldName: 'workspaces',
                    icon: 'fts-icon-menu-caret-down'
                  }"
                  comfort-design
                >
                  <fts-select
                    slot="formElement"
                    slot-scope="{ formData, item }"
                    label="name"
                    :reduce="(x) => x"
                    :filterable="false"
                    multiple
                    :options="workspaceOptions"
                    v-model="formData[item.fieldName]"
                    comfort-design
                    @search="fetchWorkspaceOptions"
                    @search:focus="preloadWorkspaces"
                  />
                </fts-form-element>
              </div>
              <!-- teammate instructions: module-level ai_instruction on the canvas. GNey
                   follows these whenever it works with this module (module chat,
                   cross-module reads, automated runs); they apply on top of the workspace
                   instructions and ride the normal module save. -->
              <div class="fts-col">
                <div class="fts-form-group">
                  <fts-form-label placement="top" icon="fts-icon-magic-lamp">
                    Teammate instructions
                  </fts-form-label>
                  <div class="fts-form-helper">
                    Standing instructions GNey follows whenever it works with this module.
                    Saved with the module design; applies on top of the workspace instructions.
                  </div>
                  <textarea
                    v-model="moduleAiInstruction"
                    class="console-teammate-instruction-input"
                    rows="4"
                    maxlength="2000"
                    placeholder="e.g. Job logs are billed weekly — never propose closing one with unbilled hours. Always check the SLA date before proposing a close."
                  />
                  <div class="console-teammate-instruction-meta">
                    {{ moduleAiInstruction.length }} / 2000
                  </div>
                </div>
              </div>
              <!-- model -->
              <div class="fts-col" v-if="isInternal">
                <div class="fts-form-group">
                  <fts-form-label placement="top" icon="fts-icon-code">
                    Model (Script) <fts-release-badge alpha class="fts-align-top" />
                  </fts-form-label>
                  <div class="fts-form-helper">
                    Access to the module's model in the script editor
                  </div>
                  <fts-btn
                    sm
                    outline
                    text-transform-init
                    @click="openCanvasScriptModal"
                  >
                    Update Model
                  </fts-btn>
                </div>
              </div>
              <!-- packing module -->
              <div class="fts-col" v-if="isInternal">
                <div class="fts-form-group">
                  <fts-form-label placement="top">
                    Packing Module <fts-release-badge alpha class="fts-align-top" />
                  </fts-form-label>
                  <div class="fts-form-helper">
                    Pack a module so can use again in other workspace
                  </div>
                  <fts-btn
                    sm
                    outline
                    text-transform-init
                    @click="packModule"
                  >
                    Pack Module
                  </fts-btn>
                </div>
              </div>
              <!--connector-->
              <template v-if="canvas.connector">
                <div class="fts-col">
                  <div class="console-form-legend">Connector</div>
                </div>
                <fts-module-connector-design
                  v-model="canvas.connector"
                  :form="connectorForm"
                  container-class="fts-row gutters"
                  :connector-types="connectorTypes"
                  connector-type-class="fts-col-6 fts-offset-r-6"
                  form-item-class="fts-col-6"
                  class="fts-col"
                />
                <!-- mapping -->
                <div class="fts-col">
                  <fts-form-element
                    :form-data="canvas"
                    :item="{ fieldName: 'mappingText', type: 16 }"
                    comfort-design
                  />
                </div>
              </template>
              <!-- old -->
              <div class="fts-col">
                <div class="fts-col fts-col-md-6 fts-form-group">
                  <fts-form-label placement="top" icon="fts-icon-text-area">
                    Connector
                  </fts-form-label>
                  <fts-text-area
                    :formData="canvas"
                    :item="{ fieldName: 'connectorText' }"
                  ></fts-text-area>
                </div>
              </div>
            </div>
          </fts-transition-expand>
        </template>
        <!-- create -->
        <template v-else>
          <div class="console-card-create-action fts-text-right">
            <fts-btn text :disabled="submitting" @click="cancelCreateModule">Cancel</fts-btn>
            <fts-btn
              color="secondary"
              box-shadow-none
              :loading="submitting"
              @click="createModule"
            >Create Module</fts-btn>
          </div>
        </template>
      </div>
    </div>
    <!-- content -->
    <template v-if="vectorId">
      <template>
        <!-- search -->
        <div class="console-search" ref="consoleSearchInput">
          <fts-console-search-input
            v-model="searchKeyword"
            placeholder="Type something to filter"
            @input:search="filter"
            @input:clear="clearFilter"
            @keydown:tab="switchSuggest"
            @keydown:enter="submitSuggest"
            ref="searchKeyword"
          />
        </div>
        <!-- suggestion -->
        <div id="searchSuggestion" class="console-suggest">
          <div class="console-suggest-content" v-if="suggestions && suggestions.length">
            <div class="console-suggest-title">Suggestions</div>
            <div class="console-suggest-desc">Press enter to select. Press tab to switch.</div>

            <div class="console-suggest-results">
              <div
                class="console-suggest-result"
                v-for="(x, ind) in suggestions"
                :key="x.id"
                @mouseover="setSuggesMouseHover(x, true)"
                @mouseleave="setSuggesMouseHover(x)"
              >
                <div
                  class="console-suggest-card"
                  :class="{ focused: ind === focusSuggest }"
                  @click="selectSuggest(ind)"
                >
                  <div class="console-suggest-card-header">
                    <font-awesome-layers class="console-suggest-card-check">
                      <font-awesome-icon :icon="['far', 'circle']" />
                      <font-awesome-icon
                        :icon="['fas', 'check']"
                        transform="shrink-8"
                        v-if="false"
                      />
                    </font-awesome-layers>
                    <div class="console-suggest-card-title">{{ x.section }}</div>
                  </div>
                  <div class="console-suggest-card-body">
                    <!-- information -->
                    <div class="console-suggest-element" v-if="x.section == 'information'">
                      <i
                        class="console-suggest-element-icon fts-text-secondary"
                        :class="informationIcon(x.type)"
                      ></i>
                      <div class="console-suggest-element-name">{{ x.displayName }}</div>
                    </div>
                    <!-- activity -->
                    <div class="console-suggest-element" v-else-if="x.section == 'activity'">
                      <fts-moji :emoji="x.emoji" :size="19" class="suggest-card-icon" />
                      <div class="console-suggest-element-name">{{ x.name }}</div>
                    </div>
                    <!-- state -->
                    <fts-state
                      :state="{ name: x.name, color: x.color }"
                      class="console-suggest-state"
                      v-else-if="x.section == 'state'"
                    />
                    <!-- actions -->
                    <fts-btn
                      sm
                      icon
                      color="primary"
                      class="console-suggest-card-action"
                      @click.native.prevent="editSuggest(x)"
                      v-if="x.mouseHover"
                    >
                      <font-awesome-icon :icon="['fas', 'edit']" />
                    </fts-btn>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <!-- cards -->
      <div class="console-section">
        <template v-for="x in cardGroup">
          <div
            class="console-section-wrapper"
            :class="x.name"
            v-if="x.group.length"
            :key="`m-${vectorId}-${x.name}`"
          >
            <template v-for="card in x.group">
              <div class="console-card" v-if="relyOnReady(card)" :key="card.name">
                <!-- header -->
                <div class="console-card-header">
                  <div class="console-card-title-wrapper">
                    <div class="console-card-title">
                      {{ card.displayName }}
                      <fts-release-badge class="fts-align-super" v-if="card.beta" />
                    </div>
                    <fts-btn
                      sm
                      text
                      color="primary"
                      text-transform-init
                      class="console-card-action-create"
                      @click="openConsoleElement(card)"
                      v-if="!card.disabled && card.creatable"
                    >
                      <font-awesome-icon :icon="['fas', 'plus']" /> Add
                    </fts-btn>
                    <fts-btn
                      sm
                      text
                      color="primary"
                      text-transform-init
                      class="console-card-action-create"
                      @click="openConsoleElement(card, { engine: 'htmljs' })"
                      v-if="isInternal && !card.disabled && card.creatable && card.name === 'document'"
                    >
                      <font-awesome-icon :icon="['fas', 'plus']" /> Add HTML/JS <fts-release-badge alpha class="fts-align-super" />
                    </fts-btn>
                  </div>
                </div>
                <!-- body -->
                <div class="console-card-body">
                  <!-- the page design (canvas.listDesign, R13 / §5.7): pinned above the views, never one of them -->
                  <div class="console-card-pinned" v-if="card.name === 'menu' && !card.disabled">
                    <div class="console-card-pinned__row" @click="openListDesign()">
                      <fts-moji emoji="🗂️" :size="14" />
                      <span class="console-card-pinned__name">Default</span>
                      <span class="console-card-pinned__tag">system</span>
                      <span class="console-card-pinned__hint">
                        {{ $store.getters.canvasHasViews ? 'Not shown while views exist; views without their own card inherit it.' : 'The page everyone sees while the module has no views.' }}
                      </span>
                    </div>
                  </div>
                  <!-- capability unsupported -->
                  <div class="console-card-disabled" v-if="card.disabled">
                    Module doesn't support this capability
                  </div>

                  <div
                    :id="`console-card-element-${card.name}`"
                    class="console-card-element fts-scrollbar"
                    v-else-if="card.fieldName === 'information' || Boolean(mutableCanvas[card.fieldName] && mutableCanvas[card.fieldName].length)"
                  >
                    <fts-console-element
                      :parent-id="`console-card-element-${card.name}`"
                      :canvas.sync="mutableCanvas"
                      :item="card.fieldName"
                      :type="card.name"
                      :label="card.displayName"
                      :page-size="card.pageSize"
                      :draggable="card.draggable"
                      :creatable="card.creatable"
                      :editable="card.editable"
                      :authorization="card.authorization"
                      :authorization-rule="card.authorizationRule"
                      :automation="card.automation"
                      :disable-list="consoleElementDisableList"
                      @update="openConsoleElement(card, $event)"
                      @openAutomation="showAutomationDesignModal"
                      @openSubscription="openSubscriptionDesignModal"
                      @moduleSelected="$emit('module:selected', $event)"
                    />
                    <!-- information:system default -->
                    <fts-console-element
                      :canvas="informationSystemDefault"
                      :item="card.fieldName"
                      :type="card.name"
                      :label="card.displayName"
                      :page-size="0"
                      :draggable="false"
                      :creatable="false"
                      :editable="false"
                      :authorization="false"
                      :authorization-rule="(x) => x.id === 'state'"
                      :automation="false"
                      system
                      @update="openConsoleElement(card, $event)"
                      @openAutomation="showAutomationDesignModal"
                      @openSubscription="openSubscriptionDesignModal"
                      @moduleSelected="$emit('module:selected', $event)"
                      v-if="card.name === 'information'"
                    />
                  </div>
                  <!-- footer -->
                  <div class="console-card-body-divider" v-if="isDevelopment && card.name == 'information'">
                    <fts-information-design-shortcut
                      :modules="workspaceModules"
                      :information="information"
                      @option:create="({ formData, requiresSetting }) => createInformation(formData, requiresSetting)"
                    />
                  </div>

                  <div
                    class="console-card-body__footer"
                    v-if="!card.disabled && card.automation && beta"
                  >
                    <fts-release-badge />
                    <div class="fts-inline-spacer-5"></div>
                    <!-- automation -->
                    <div
                      class="console-card-automation-container"
                      v-if="
                        getAutomation(card.fieldName) &&
                        getAutomation(card.fieldName).length
                      "
                    >
                      <fts-event
                        v-for="event in getAutomation(card.fieldName)"
                        :key="event.id"
                        :event="event"
                        @open="showAutomationDesignModal"
                      />
                    </div>
                    <!-- actions -->
                    <div class="console-card-content-actions">
                      <!-- notification -->
                      <fts-event-toggle
                        event-type="notification"
                        class="console-card-event-btn"
                        @open="addAutomation(card, 'notification')"
                      />
                      <!-- automation -->
                      <fts-event-toggle
                        class="console-card-event-btn"
                        @open="addAutomation(card, 'automation')"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </template>
      </div>
      <!-- no found -->
      <div class="console-no-result" v-if="!filterCard.length">
        <div class="no-result-label">No results found</div>
        <div class="no-result-desc">Try different search term</div>
      </div>
    </template>

    <fts-pack-module-modal ref="ftsPackModuleModal" />
    <fts-information-design-modal
      :is-capable-add-information="isCapableOf(SYSTEM_CAPABILITY.AddInformation)"
      :is-capable-change-information="isCapableOf(SYSTEM_CAPABILITY.ChangeInformation)"
      ref="ftsInformationDesignModal"
    />
    <fts-document-design-modal ref="ftsDocumentDesignModal" />
    <fts-html-js-design-modal
      ref="ftsHtmlJsDesignModal"
      :load-sample-data="htmljsLoadSampleData"
      :load-entries="htmljsLoadEntries"
      :load-listings="htmljsLoadListings"
      :persist-bundle="htmljsPersistBundle"
      :load-bundle="htmljsLoadBundle"
      :generate-bundle="htmljsGenerateBundle"
      :render-pdf="htmljsRenderPdf"
      :evaluate-file-name="htmljsEvaluateFileName"
    />
    <fts-activity-design-modal
      :is-capable-write="isCapableOf(SYSTEM_CAPABILITY.Write)"
      :is-capable-workflow="isCapableOf(SYSTEM_CAPABILITY.Workflow)"
      :is-capable-add-information="isCapableOf(SYSTEM_CAPABILITY.AddInformation)"
      ref="ftsActivityDesignModal"
    />
    <fts-standard-activity-design-modal
      :is-capable-write="isCapableOf(SYSTEM_CAPABILITY.Write)"
      :is-capable-workflow="isCapableOf(SYSTEM_CAPABILITY.Workflow)"
      :is-capable-add-information="isCapableOf(SYSTEM_CAPABILITY.AddInformation)"
      ref="ftsStandardActivityDesignModal"
    />
    <fts-listing-design-modal ref="listingDesign" />
    <fts-state-design-modal ref="ftsStateDesignModal" />
    <fts-flow-design-modal ref="ftsFlowDesignModal" />
    <fts-action-design-modal ref="ftsActionDesignModal" />
    <fts-automation-design-modal ref="ftsAutomationDesignModal" />
    <fts-logic-service-design-modal ref="ftsLogicServiceDesignModal" />
    <fts-import-design-modal ref="ftsImportDesignModal" />
    <fts-subscription-design-modal ref="ftsSubscriptionDesignModal" />
    <fts-module-automation-panel-modal ref="ftsModuleAutomationPanelModal" />
    <fts-module-history-modal ref="ftsModuleHistoryModal" />
    <fts-script-editor-modal ref="ftsScriptEditorModal" />
    <!-- canvas guard (D11): referrer checklist for modal-initiated deletes -->
    <fts-canvas-guard-dialog ref="ftsCanvasGuardDialog" />
  </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";

import { store } from "../../components/store/canvasDesignStore";
import { authorizationStore } from "../store/authorizationStore";
import commons from "../commons/commons";
import { SYSTEM_CAPABILITY, isCapableOf } from "../commons/flags";
import designValidator from "../commons/designValidator";
import canvasDesign from "../commons/canvasDesign";
import connectorCommons from "../commons/connector";
import formDesign from '../commons/formDesign';
import mixin from "../mixins/mixin";

import FTSTransitionExpand from "../elements/FTSTransitionExpand";
import FTSConsoleElement from "./FTSConsoleElement";
import FTSCanvasGuardDialog from "./FTSCanvasGuardDialog";
import FTSCanvasApplyHistory from "./FTSCanvasApplyHistory";
import FTSEmoji from "../elements/FTSEmoji";
import FTSContenteditable from "../elements/FTSContenteditable";
import FTSSelect from "../elements/FTSSelect";
import FTSToggleSwitch from "../elements/FTSToggleSwitch";
import FTSSpinner from "../elements/FTSSpinner";
import FTSTextArea from "../elements/FTSTextArea";
import FTSBtn from "../elements/FTSBtn";
import FTSState from "../elements/FTSState";
import FTSMoji from "../elements/FTSMoji";
import FTSAuth from "./FTSAuth";
import FTSTippy from "../elements/FTSTippy";
import FTSProfile from "../elements/FTSProfile";
import FTSEvent from "../design/event/FTSEvent";
import { isSchedule, newScheduleTemplate } from "../design/schedule/scheduleLogic";
import FTSEventToggle from "../design/event/FTSEventToggle";
import FTSFormElement from "../elements/FTSFormElement";
import FTSFormLabel from "../elements/FTSFormLabel";
import FTSModuleConnectorDesign from "./FTSModuleConnectorDesign";

import FTSInformationDesignModal from "./FTSInformationDesignModal";
import FTSDocumentDesignModal from "./FTSDocumentDesignModal";
import FTSHtmlJsDesignModal from "./FTSHtmlJsDesignModal";
import FTSActivityDesignModal from "./FTSActivityDesignModal";
import FTSStandardActivityDesignModal from "./FTSStandardActivityDesignModal";
import FTSListingDesignModal from "./FTSListingDesignModal";
import FTSStateDesignModal from "./FTSStateDesignModal";
import FTSFlowDesignModal from "./FTSFlowDesignModal";
import FTSActionDesignModal from "./FTSActionDesignModal";
import FTSLogicServiceDesignModal from "./FTSLogicServiceDesignModal";
import FTSAutomationDesignModal from "./FTSAutomationDesignModal";
import FTSImportDesignModal from "./FTSImportDesignModal";
import FTSPackModuleModal from "./FTSPackModuleModal";
import FTSConsoleSearchInput from "./console/FTSConsoleSearchInput";
import FTSSubscriptionDesignModal from "./FTSSubscriptionDesignModal";
import FTSModuleAutomationPanelModal from "./FTSModuleAutomationPanelModal";
import FTSModuleHistoryModal from "./FTSModuleHistoryModal";
import FTSScriptEditorModal from './FTSScriptEditorModal';
import FTSInformationDesignShortcut from './FTSInformationDesignShortcut';

import NotificationPanel from "./../administration/NotificationControlPanel.vue";

import VueScrollTo from "vue-scrollto";
Vue.use(VueScrollTo);

import _ from "lodash";

import Vuex from "vuex";
Vue.use(Vuex);

import Vuelidate from "vuelidate";
Vue.use(Vuelidate);

export default {
  props: {
    canvasProps: Object,
    baseServer: String,
    vectorId: [String, Number],
    vectorProps: {
      type: Object,
      default: () => ({})
    },
    validator: Object,
    workspaceId: Number
  },
  components: {
    "fts-transition-expand": FTSTransitionExpand,
    "fts-console-element": FTSConsoleElement,
    "fts-canvas-guard-dialog": FTSCanvasGuardDialog,
    "fts-canvas-apply-history": FTSCanvasApplyHistory,
    "fts-emoji": FTSEmoji,
    "fts-contenteditable": FTSContenteditable,
    "fts-select": FTSSelect,
    "fts-toggle-switch": FTSToggleSwitch,
    "fts-spinner": FTSSpinner,
    "fts-text-area": FTSTextArea,
    "fts-btn": FTSBtn,
    "fts-state": FTSState,
    "fts-moji": FTSMoji,
    "fts-auth": FTSAuth,
    "fts-tippy": FTSTippy,
    "fts-profile": FTSProfile,
    "fts-event": FTSEvent,
    "fts-event-toggle": FTSEventToggle,
    "fts-form-element": FTSFormElement,
    "fts-form-label": FTSFormLabel,
    "fts-module-connector-design": FTSModuleConnectorDesign,
    "fts-information-design-modal": FTSInformationDesignModal,
    "fts-document-design-modal": FTSDocumentDesignModal,
    "fts-html-js-design-modal": FTSHtmlJsDesignModal,
    "fts-activity-design-modal": FTSActivityDesignModal,
    "fts-standard-activity-design-modal": FTSStandardActivityDesignModal,
    "fts-listing-design-modal": FTSListingDesignModal,
    "fts-state-design-modal": FTSStateDesignModal,
    "fts-flow-design-modal": FTSFlowDesignModal,
    "fts-action-design-modal": FTSActionDesignModal,
    "fts-logic-service-design-modal": FTSLogicServiceDesignModal,
    "fts-automation-design-modal": FTSAutomationDesignModal,
    "fts-import-design-modal": FTSImportDesignModal,
    "fts-pack-module-modal": FTSPackModuleModal,
    "fts-console-search-input": FTSConsoleSearchInput,
    "fts-subscription-design-modal": FTSSubscriptionDesignModal,
    "fts-module-automation-panel-modal": FTSModuleAutomationPanelModal,
    "fts-module-history-modal": FTSModuleHistoryModal,
    "fts-script-editor-modal": FTSScriptEditorModal,
    "fts-information-design-shortcut": FTSInformationDesignShortcut,
    "notification-panel": NotificationPanel
  },
  store,
  mixins: [mixin],
  data: () => ({
    SYSTEM_CAPABILITY: Object.freeze(SYSTEM_CAPABILITY),
    card: {
      name: "vector",
      fieldName: "vector",
      displayName: "Vector",
      learnMore: `${commons.communityURL}/t/fetias101-module/79/2`,
      description:
        "module is part of your business app like customers, employees, jobs or tickets"
    },
    workspaceOptions: [],
    canvas: {},
    vector: {},
    /* draggable */
    dragOptions: commons.dragOptions,
    /* module (create) */
    submitting: false,
    /* control */
    moduleAdvancedSettings: false,
    showNotificationPanel: false,
    /* connector */
    connectorTypes: connectorCommons.connectors.filter(x => !x.upcoming),
    /* search */
    searchKeyword: null,
    focusSuggest: 0,
    /* undefined */
    mapping: null,
    connector: null,
    packages: [],
  }),
  validations() {
    let vector = {};
    vector = designValidator.moduleDesign(this.$store.getters.workspaceModules);
    return { vector };
  },
  methods: {
    moment,
    // Whether an information field may carry per-field access rules. Mirrors the server-side
    // validation (VectorSubmissionService.validateInformationAuthorizations): locking these
    // would leak or break channels the field filter cannot cover (title/notifications,
    // profile identity, entry date, assignment/due workflow).
    isInformationLockable(info) {
      if (!info || info.system) return false;
      if (info.fieldName === 'username' || info.fieldName === 'date') return false;
      if (info.assignee || info.unassignee || info.addAssignee || info.due) return false;
      const title = (this.mutableCanvas && this.mutableCanvas.title) || '';
      if (title && info.displayName && title.includes(info.displayName)) return false;
      return true;
    },
    // --- htmljs document editor wiring (passed to FTSHtmlJsDesignModal) ---
    // List the module's entries so the designer can choose which one to render in the live preview.
    // listingId comes from the preview bar's Listing pick; 'everything' lists all entries regardless
    // of per-listing filters. Only the first `limit` rows are fetched — this feeds a sample picker.
    async htmljsLoadEntries(listingId, limit) {
      // The studio canvas is data.extension (no id); the moduleId is the vectorId.
      const moduleId = this.$store.state.vectorId;
      if (!moduleId) return [];
      const res = await this.$store.dispatch("docEntries", {
        moduleId,
        listingId: listingId || "everything",
        pageSize: limit || 50,
      });
      const d = res && res.data;
      // Tolerate the few shapes /api/workspace/list can return across legacy / x-list.
      const list =
        (d && d.data && d.data.list) ||
        (d && d.list) ||
        (Array.isArray(d && d.data) ? d.data : null) ||
        (Array.isArray(d) ? d : []) ||
        [];
      return list.map(x => ({ id: x.id, title: x.title || x.name || x.id }));
    },
    // Live preview data: the typed DocumentDataModel for the chosen sample entry, via /doc/data.
    async htmljsLoadSampleData(item, entryId, dataSource, listingId) {
      const moduleId = this.$store.state.vectorId;
      if (!moduleId) return {};
      // Report preview: fetch the collection model (entries[]) over the listing chosen in the preview
      // bar; "everything" lists every row regardless of per-listing filters. The backend honours the
      // request-level dataSource only on this unsaved (documentId:"") preview path.
      if (dataSource === "Entries") {
        const res = await this.$store.dispatch("docData", {
          moduleId,
          documentId: "",
          dataSource: "Entries",
          listingId: listingId || "everything",
        });
        return res.data;
      }
      entryId = entryId || (item && (item.testLocus || item.testLocusId));
      if (!entryId) return {};
      const res = await this.$store.dispatch("docData", {
        moduleId,
        entryId,
        // Live preview renders the in-editor bundle, so pass no template id — an unsaved
        // document id would trip the AuthorizedDocuments gate ("Template not accessible").
        documentId: ""
      });
      return res.data;
    },
    // Persist the bundle body out-of-canvas; returns { ref, contentHash } for the manifest.
    async htmljsPersistBundle(bundle, page) {
      const moduleId = this.$store.state.vectorId;
      const res = await this.$store.dispatch("docBundle", {
        moduleId,
        documentId: this.$refs.ftsHtmlJsDesignModal.item.id,
        name: this.$refs.ftsHtmlJsDesignModal.name,
        bundle,
        page
      });
      return res.data;
    },
    // Fetch a saved bundle by ref for editing, via GET /doc/bundle/{ref}.
    async htmljsLoadBundle(ref) {
      const res = await this.$store.dispatch("docBundleGet", ref);
      return res.data;
    },
    // DEBUG-only: byte-exact server render of the CURRENT in-editor bundle (POST /doc/render → PDF
    // blob). Studio-only "compare against the real output" button; the FE downloads client-side.
    // Sends the inline bundle/page so it renders the open edits, not the last-saved template.
    async htmljsRenderPdf({ bundle, page, entryId, item, name }) {
      const moduleId = this.$store.state.vectorId;
      const res = await this.$store.dispatch("docRender", {
        moduleId,
        entryId: entryId || (item && (item.testLocus || item.testLocusId)),
        documentId: (item && item.id) || "",
        name,
        bundle,
        page,
      });
      return res.data;
    },
    // AI teammate document generation — a service that exists ONLY behind this modal. Sends the
    // brief plus the editor's current bundle/page (so a follow-up brief revises the open draft);
    // the returned draft replaces the editor state and renders in the live preview. Nothing is
    // stored until the designer clicks Save (the normal persistBundle + module-save path).
    // The file-name formula's live check: the UNSAVED formula over the previewed entry, evaluated by
    // the server's native engine exactly as downloads and e-mail attachments will be.
    async htmljsEvaluateFileName({ formula, entryId, name, inputForm }) {
      const moduleId = this.$store.state.vectorId;
      if (!moduleId) return null;
      const res = await this.$store.dispatch("docFileName", { moduleId, entryId, name, formula, inputForm });
      return res.data;
    },
    // The module's listings (canvas menus) for the fixed-report scope picker (Mode 3). Sync.
    htmljsLoadListings() {
      const menus = this.$store.getters.canvasMenus || [];
      return (Array.isArray(menus) ? menus : [])
        .filter((m) => m && m.id && m.id !== "defaultListing")
        .map((m) => ({ id: m.id, name: m.name || m.text || m.id }));
    },
    async htmljsGenerateBundle({ name, brief, bundle, page, dataSource }) {
      const moduleId = this.$store.state.vectorId;
      const res = await this.$store.dispatch("teammateDocument", {
        moduleId,
        name,
        brief,
        bundle,
        page,
        // "Entry" | "Entries" — drives report-mode generation on the backend (entries[] / grouping).
        dataSource,
      });
      return res.data;
    },
    isCapableOf(type) {
      return isCapableOf(type, this.$store.getters.workspaceModules, this.vectorId);
    },
    relyOnReady(card) {
      if (!card.relyOn || card.relyOn.length == 0) {
        return true;
      }

      if (this.canvas.information && this.canvas.information.length) {
        return true;
      }

      return false;
    },
    async packModule() {
      await this.$refs.ftsPackModuleModal.show();
    },
    reset() {
      this.$emit("scrollTo");
      this.moduleAdvancedSettings = false;
      this.searchKeyword = null;
      this.focusSuggest = 0;
      this.$store.dispatch("filterElement", null);
    },
    moduleNameError() {
      return designValidator.moduleDesignError(this.$v.vector, "name");
    },
    updateModuleName(name) {
      this.$set(this.vector, "name", name);

      if (this.vectorId && this.$store.state.vector) {
        const vector = {
          ...this.$store.state.vector,
          name,
        };
        this.$store.dispatch("updateModule", vector);
        this.vector = vector;
      }

      this.debounceInput();
      this.$v.vector.name.$touch();
    },
    async createModule() {
      this.$v.vector.$touch();
      if (this.$v.vector.$invalid) {
        this.moduleNameError();
        this.$emit("scrollTo");
      } else {
        this.submitting = true;
        try {
          await this.$store.dispatch("addVector", { ...this.vector });
          /* workaround to get lastest workspace lastUpdatedOn */
          await this.$store.dispatch("loadWorkspace", { id: this.workspaceId });
          /* end */
          const vectorId =
            this.$store.state.vector && this.$store.state.vector.id;
          const vectorName =
            this.$store.state.vector && this.$store.state.vector.name;
          this.$emit("module:created");
          this.$toasted.success(
            `${
              vectorName ? `Module: ${vectorName}` : "Module"
            } has been created.`
          );
          if (vectorId)
            this.$router.push({
              query: { id: commons.encodeids(vectorId) }
            });
        } catch (err) {
          console.error(err);
          this.$toasted.global.error({
            response: err.response,
            message: "Unable create module"
          });
        } finally {
          this.submitting = false;
        }
      }
    },
    cancelCreateModule() {
      this.$emit("cancel");
    },
    debounceInput: _.debounce(async function () {
      if (this.vector.name) {
        try {
          const res = await this.$store.dispatch("ai", {
            type: "emoji",
            text: this.vector.name
          });
          res.data && this.$set(this.vector, "emoji", res.data);
        } catch(err) { console.error(err); }
      }
    }, 800),
    checkContent(fieldName, name) {
      let canvasFields = commons.isArray(this.canvas[fieldName]) ? this.canvas[fieldName] : [];

      if (!this.beta && name === "standardActivity") {
        canvasFields = canvasFields.filter(x => x.id !== "print");
      }
      
      if (
        name === "information" &&
        this.informationSystemDefault[fieldName] &&
        this.informationSystemDefault[fieldName].some(x =>
          x.displayName && 
          x.displayName.toLowerCase().indexOf(this.searchKeywordToLower) > -1
        )
      ) {
        return true;
      }

      const canvasFieldsMatched = canvasFields.some(x => {
        if (
          (x.displayName && x.displayName.toLowerCase().indexOf(this.searchKeywordToLower) > -1) ||
          (x.name && x.name.toLowerCase().indexOf(this.searchKeywordToLower) > -1)
        ) {
          return true;
        }

        if (
          name === "information" && 
          Array.isArray(x.fields) && 
          x.fields.some(y =>
            y.displayName && 
            y.displayName.toLowerCase().indexOf(this.searchKeywordToLower) > -1
          )
        ) {
          return true;
        }

        if (name === "flow") {
          if (x.activityId) {
            const flowActivity = this.$store.getters.canvasActivities.find(y => y.id === x.activityId);

            if (
              flowActivity && 
              flowActivity.name && 
              flowActivity.name.toLowerCase().indexOf(this.searchKeywordToLower) > -1
            ) {
              return true;
            }
          }

          const isFlowState = (list) => 
            list.some(y => {
              const flowState = this.$store.getters.canvasStates.find(z => z.id === y);
              return flowState && flowState.name && flowState.name.toLowerCase().indexOf(this.searchKeywordToLower) > -1;
            });

          if (x.fromStateId && isFlowState([x.fromStateId])) {
            return true;
          }

          if (x.conditional) {
            if (
              Array.isArray(x.conditionalStates) && 
              isFlowState(x.conditionalStates.map(y => y.toStateId))
            ) {
              return true;
            }
          } else if (x.toStateId && isFlowState([x.toStateId])) {
            return true;
          }
        }

        if (this.isInternal && x.id === this.searchKeyword) {
          return true;
        }
      });

      if (canvasFieldsMatched == true) {
        return true;
      }

      if (name !== "flow" && Array.isArray(this.canvas.triggers)) {
        const hasTriggerMatch = this.canvas.triggers.some(x => {
          if (x.type === name) {
            const isDescriptionMatch = x.description ? x.description.toLowerCase().includes(this.searchKeywordToLower) : false;
            let isActivityIdMatch = false;

            if (this.isInternal) {
              try {
                isActivityIdMatch = JSON.stringify(x).includes(`"activityId":"${this.searchKeyword}"`);
              } catch(err) {
                console.error('Error stringifying object:', err);
              }
            }

            return isDescriptionMatch || isActivityIdMatch;
          }

          return false;
        });

        if (hasTriggerMatch) {
          return true;
        }
      }

      return false;
    },
    /* workspace options */
    async loadWorkspaces(search) {
      let result = await this.$store.dispatch("searchWorkspace", {
        search: search || ""
      });
      this.workspaceOptions = result;
      if (
        this.$store.state.workspace &&
        !this.workspaceOptions.some(x => x.id == this.$store.state.workspace.id)
      ) {
        this.workspaceOptions.push(this.$store.state.workspace);
      }
    },
    async preloadWorkspaces(loading) {
      loading(true);
      await this.loadWorkspaces();
      loading(false);
    },
    searchWorkspace: _.debounce(async (loading, search, vm) => {
      await vm.loadWorkspaces(search);
      loading(false);
    }, 300),
    async fetchWorkspaceOptions({ searchText, loading }) {
      loading(true);
      await this.searchWorkspace(loading, searchText, this);
    },
    /* console element */
    /** D19: open an applied automation in the designer for review, by trigger id. */
    openGneyAutomation({ triggerId }) {
      const trigger = (this.canvas.triggers || []).find(t => t && t.id == triggerId);
      if (trigger) this.showAutomationDesignModal(trigger);
    },

    /* ---- GNey htmljs document drafts (D16) ------------------------------------------ */
    /** Write a document manifest into the draft canvas — the same generic upsert-by-id the
        console's own document flow performs — and optionally trigger the module save. */
    writeGneyDocumentManifest(manifest, save) {
      if (!manifest || manifest.id == null) return;
      if (!Array.isArray(this.canvas.documents)) this.$set(this.canvas, "documents", []);
      const items = this.canvas.documents;
      const at = items.findIndex(x => x && x.id == manifest.id);
      if (at > -1) this.$set(items, at, manifest);
      else items.push(manifest);
      commons.compactClassification(this.canvas);
      this.$store.dispatch("updateVectorCanvas", this.canvas);
      if (save) this.$emit("update");
    },
    /** Open the htmljs editor on a GNey-drafted bundle. Save inside the modal commits via
        the console's normal document path and reports back to the card that opened it. */
    async openGneyHtmlJsDraft({ item, nonce }) {
      if (!this.$refs.ftsHtmlJsDesignModal) return;
      const result = await this.$refs.ftsHtmlJsDesignModal.show(item, (formData) => {
        this.writeGneyDocumentManifest(formData, true);
        window.eventBus.$emit("gney:htmlJsDraftSaved", { nonce, name: formData && formData.name });
      });
      // The modal resolves the final manifest after a save-and-close; mirror the console's
      // own flow by writing that last state too (no second save — the callback saved).
      if (result != null && result !== true) this.writeGneyDocumentManifest(result, false);
    },

    /* D11/D14: guard for modal-initiated deletes (the modal's own confirm resolved true).
       Returns true when the guard handled it (applied through the ops engine — recorded,
       revertible — or cancelled); false = gate off or "Delete anyway" → caller runs the
       legacy canvasDesign.delete* path. */
    async guardedConsoleDelete(address, unitLabel, subject) {
      if (!this.$store.getters.canvasOpsEnabled) return false;
      let composed;
      try {
        composed = await this.$store.dispatch('guardedRemoveCompose', { address });
      } catch (error) {
        console.error("canvas guard compose failed — falling back to the legacy delete", error);
        return false;
      }
      if (!composed || composed.errors) return false;

      const res = await this.$refs.ftsCanvasGuardDialog.show({
        mode: 'delete',
        unitLabel,
        subject,
        composed,
      });
      if (res === false) return true; // cancelled — nothing deleted
      if (res.mode === 'legacy') return false;

      const result = await this.$store.dispatch('applyCanvasOps', {
        ops: res.ops,
        authRows: res.authRows,
        source: 'guard',
        label: `Delete ${unitLabel.toLowerCase()} '${subject}'`,
      });
      if (!result.ok) {
        const first = result.errors.concat(...result.ops.map(o => o.errors))[0];
        this.$toasted.global.error({
          message: first ? first.message : "The delete could not be applied cleanly.",
        });
      }
      return true;
    },
    /* The page design (canvas.listDesign, R13 / §5.7): the module page's own web and mobile columns,
       edited in the listing modal minus scope. Never a menus[] row; the card comes from the Design
       control on the page. */
    async openListDesign() {
      const vm = this;
      const design = canvasDesign.listDesignOf(_.cloneDeep(vm.canvas));
      const item = {
        id: 'default',
        name: 'Default',
        emoji: '🗂️',
        carrier: true,
        list: design.list,
        mobileListDesign: design.mobileListDesign || { columns: [] },
        filter: { items: [] },
        defaultFilter: { items: [] },
        defaultAppFilter: { items: [] }
      };
      const apply = (formData, save) => {
        const target = canvasDesign.listDesignOf(vm.canvas);
        vm.$set(target, 'list', { columns: (formData.list && formData.list.columns) || [] });
        vm.$set(target, 'mobileListDesign', { columns: (formData.mobileListDesign && formData.mobileListDesign.columns) || [] });
        commons.compactClassification(vm.canvas);
        vm.$store.dispatch("updateVectorCanvas", vm.canvas);
        if (save) vm.$emit("update");
      };
      const result = await this.$refs.listingDesign.show(item, formData => apply(formData, true));
      if (result != null && result != true) apply(result, false);
    },
    async openConsoleElement(type, selected, params) {
      let vm = this;
      let item = { ...selected };

      if (!item.id && type.name !== "information") {
        item = {
          id: type.name === "menu" ? commons.nanoID() : commons.newUUID(),
          ...selected
        };

        if (type.name == "document") {
          item.version = 1.0;
        }
      }
      /* update */
      function set(result, save) {
        let items = vm.canvas[type.fieldName];

        if (!items) {
          items = [];
        }
        const findIndex = items.findIndex(x => x.id == result.id);
        if (findIndex > -1) {
          vm.$set(items, findIndex, result);
        } else if (type.name === "information") {
          let findDeep = _.findDeep(items, x => x.id == result.id, {
            childrenPath: "fields"
          });
          if (findDeep && findDeep.parent && findDeep.key) {
            vm.$set(findDeep.parent.fields, findDeep.key, result);
          } else {
            items.push(result);
          }
        } else {
          items.push(result);
        }
        /* add information fields to forms */
        if (
          (type.name === 'standardActivity' && result.design) ||
          (type.name === 'information' && (result.type == 14 || result.type == 15))
        ) {
          formDesign.addInformationFieldsToForms(vm.canvas);
        }
        /* The legacy "defaultListing" placeholder row is no longer removed here (SS04983): its
           design is lifted into canvas.listDesign by the server on save and the row stays until the
           Phase 2 app is the floor (R9). It is hidden from every design surface instead. */
        /* update canvas */
        vm.$set(vm.canvas, type.fieldName, items);
        /* compact classification */
        commons.compactClassification(vm.canvas);
        /* update vector canvas */
        vm.$store.dispatch("updateVectorCanvas", vm.canvas);
        /* save canvas */
        if (save) {
          vm.$emit("update");
        }
      }
      /* remove */
      function remove() {
        let items = vm.canvas[type.fieldName];
        const findIndex = items.findIndex(x => x.id == item.id);
        if (findIndex > -1) {
          items.splice(findIndex, 1);
        } else if (type.name === "information") {
          let findDeep = _.findDeep(items, x => x.id == item.id, {
            childrenPath: "fields"
          });
          if (findDeep && findDeep.parent && findDeep.key) {
            vm.$delete(findDeep.parent.fields, findDeep.key);
          }
        }
      }
      /* set alternative design */
      function setAlternativeDesign(canvas, items) {
        items.forEach(x => {
          if (x && x.type && x.extension) {
            if (x.type === "flow") {
              canvasDesign.setFlow(canvas, x.extension);
            }
          }
        });
      }
      if (type.name === "information") {
        if (item.system) {
          if (item.id === "title") {
            item.title = this.canvas.title;
          }
        } else if (!item.id) {
          item = {
            id: commons.generateFieldName(),
            fieldName: commons.generateFieldName(),
            ...selected
          };
        }
        const result = await this.$refs.ftsInformationDesignModal.show(
          item,
          null,
          continueFormData => {
            set(continueFormData);
            this.openConsoleElement(type);
          }
        );
        if (result != null && result != true) {
          if (result.system) {
            if (result.id === "title") {
              this.$set(this.canvas, "title", result.title);
            } else if (result.id == "documentId") {
              if (result.documentIdFormat) {
                this.$set(
                  this.vector,
                  "documentIdFormat",
                  result.documentIdFormat
                );
              } else {
                this.$delete(this.vector, "documentIdFormat");
              }
            }
          } else {
            set(result);
          }
        } else if (result == true) {
          if (!(await this.guardedConsoleDelete([["information", item.id]], "Information", item.displayName || item.name || item.id))) {
            canvasDesign.deleteInformation(vm.canvas, item.id);
          }
        }
      } else if (type.name === "menu") {
        const result = await this.$refs.listingDesign.show(item, formData => {
          set(formData, true);
        });
        if (result != null && result != true) {
          set(result);
        } else if (result == true) {
          if (!(await this.guardedConsoleDelete([["menus", item.id]], "Listing", item.name || item.id))) {
            canvasDesign.deleteMenu(vm.canvas, item);
          }
        }
      } else if (type.name == "document") {
        // htmljs documents use the new declarative editor; everything else keeps the legacy modal.
        const modalRef = item && item.engine === "htmljs"
          ? this.$refs.ftsHtmlJsDesignModal
          : this.$refs.ftsDocumentDesignModal;
        const result = await modalRef.show(
          item,
          formData => {
            set(formData, true);
          }
        );
        if (result != null && result != true) {
          set(result);
        } else if (result == true) {
          if (!(await this.guardedConsoleDelete([["documents", item.id]], "Document", item.name || item.id))) {
            canvasDesign.deleteDocument(vm.canvas, item);
          }
        }
      } else if (type.name === "activity") {
        const setActivity = (formData, alternativeDesign) => {
          let canvas = _.cloneDeep(this.$store.state.canvas);
          /* set activity */
          canvasDesign.setActivity(canvas, formData);
          /* alternative design */
          if (Boolean(alternativeDesign)) {
            setAlternativeDesign(canvas, alternativeDesign);
          }
          /* add information fields to forms */
          formData.design && formDesign.addInformationFieldsToForms(canvas);
          /* compact classification */
          commons.compactClassification(canvas);
          /* dispatch state */
          this.$store.dispatch("updateVectorCanvas", canvas);
        }
        const result = await this.$refs.ftsActivityDesignModal.show(
          item,
          /* save */
          (formData, alternativeDesign) => {
            setActivity(formData, alternativeDesign);
            /* save */
            this.$emit("update");
          },
          /* continue callback */
          (continueFormData, alternativeDesign) => {
            setActivity(continueFormData, alternativeDesign);
            /* open design */
            this.openConsoleElement(type);
          },
          /* navigate callback */
          (
            navigateFormData,
            navigateType,
            navigateData,
            navigateParams,
            alternativeDesign
          ) => {
            setActivity(navigateFormData, alternativeDesign);
            /* open design */
            setTimeout(() => {
              this.openConsoleElement(
                navigateType,
                navigateData,
                navigateParams
              );
            }, 500);
          },
          /* params */
          params
        );
        if (result != null && result != true) {
          // let canvas = _.cloneDeep(this.$store.state.canvas);
          if (result.formData) {
            setActivity(result.formData, result.alternativeDesign);
          } else {
            setActivity(result);
          }
        } else if (result == true) {
          if (!(await this.guardedConsoleDelete([["activities", item.id]], "Activity", item.name || item.id))) {
            canvasDesign.deleteActivity(vm.canvas, item);
          }
        }
      } else if (type.name === "standardActivity" && item.id === "import") {
        const result = await this.$refs.ftsImportDesignModal.show(
          this.canvas.import || {}
        );
        if (result != null && result != true) {
          this.$set(this.canvas, "import", result);
        } else if (result == true) {
          this.$delete(this.canvas, "import");
        }
      } else if (type.name === "standardActivity" && item.id) {
        const result = await this.$refs.ftsStandardActivityDesignModal.show(
          item,
          formData => {
            set(formData, true);
          },
          (navigateFormData, navigateType, navigateData, navigateParams) => {
            set(navigateFormData);
            /* open design */
            setTimeout(() => {
              this.openConsoleElement(
                navigateType,
                navigateData,
                navigateParams
              );
            }, 500);
          }
        );
        if (result != null && result != true) {
          set(result);
        }
      } else if (type.name === "state") {
        const result = await this.$refs.ftsStateDesignModal.show(
          item,
          null,
          continueFormData => {
            set(continueFormData);
            this.openConsoleElement(type);
          },
          (navigateFormData, navigateType, navigateData, navigateParams) => {
            set(navigateFormData);
            /* open design */
            setTimeout(() => {
              this.openConsoleElement(
                navigateType,
                navigateData,
                navigateParams
              );
            }, 500);
          }
        );
        if (result != null && result != true) {
          set(result);
        } else if (result == true) {
          if (!(await this.guardedConsoleDelete([["states", item.id]], "State", item.name || item.id))) {
            canvasDesign.deleteState(vm.canvas, item);
          }
        }
      } else if (type.name === "flow") {
        const result = await this.$refs.ftsFlowDesignModal.show(
          item,
          formData => {
            set(formData, true);
          }
        );
        if (result != null && result != true) {
          set(result);
        } else if (result == true) {
          remove();
        }
      } else if (type.name === "action") {
        const result = await this.$refs.ftsActionDesignModal.show(
          item,
          formData => {
            set(formData, true);
          }
        );
        if (result != null && result != true) {
          set(result);
        } else if (result == true) {
          if (!(await this.guardedConsoleDelete([["actions", item.id]], "Action", item.name || item.id))) {
            canvasDesign.deleteAction(vm.canvas, item);
          }
        }
      } else if (type.name === "logicService") {
        const result = await this.$refs.ftsLogicServiceDesignModal.show(
          item,
          formData => {
            set(formData, true);
          }
        );
        if (result != null && result != true) {
          set(result);
        } else if (result == true) {
          remove();
        }
      }
    },
    async createInformation(item, requiresSetting) {
      let canvas = _.cloneDeep(this.$store.state.canvas);
      let res = canvasDesign.initInformation(item);
      if (requiresSetting) {
        res = await this.$refs.ftsInformationDesignModal.show(res, null, null);
      }

      if (res && res != true) {
        canvasDesign.setInformation(canvas, res);
        this.$store.dispatch("updateVectorCanvas", canvas);
      }
    },
    // automation
    getAutomation(type) {
      return this.$store.getters.vectorTriggers.filter(
        x => x.type == type && x.item == null
      );
    },
    async showAutomationDesignModal(item) {
      await this.$refs.ftsAutomationDesignModal.show(item);
    },
    addAutomation(element, actionType) {
      this.showAutomationDesignModal({
        item: null,
        type: element.fieldName,
        trigger: "execute",
        actionType: actionType
      });
    },
    /* a new Studio schedule opens in the automation designer's schedule mode */
    addSchedule() {
      this.showAutomationDesignModal(newScheduleTemplate("notification"));
    },
    /* subscription */
    async openSubscriptionDesignModal(item) {
      /* mock data */
      let subscriptions = [
        {
          id: "orADaBp-Ov",
          name: "when changed",
          audit: {
            updatedBy: "admin",
            updatedDate: "2022-01-25T09:27:36+08:00"
          },
          source: "zapier",
          active: true
        },
        {
          id: "rP2Ak8VGMS",
          name: "when due",
          audit: {
            updatedBy: "yanzheng.lam@gneysoftware.com",
            updatedDate: "2022-01-24T09:27:36+08:00"
          },
          source: "zapier",
          active: true
        },
        {
          id: "xq3f2vG2DV",
          name: "when changed to",
          audit: {
            updatedBy: "jiaxiang.tan@gneysoftware.com",
            updatedDate: "2022-01-23T01:27:36+08:00"
          },
          source: "integromat",
          active: false
        },
        {
          id: "qVDya8yUaV",
          name: "when changed from",
          audit: {
            updatedBy: "jiaxiang.tan@gneysoftware.com",
            updatedDate: "2022-01-22T17:27:36+08:00"
          },
          source: "integromat",
          active: true
        }
      ];
      /* to-do assign data */
      const res = await this.$refs.ftsSubscriptionDesignModal.show(
        subscriptions,
        {
          title: item.name || item.displayName
        }
      );
    },
    informationIcon(type) {
      return commons.informationIcon(type);
    },
    filter() {
      const container = document.getElementsByClassName("studio-content-container").length
        ? ".studio-content-container"
        : "body";
      VueScrollTo.scrollTo("#searchSuggestion", 500, {
        container: container,
        offset: -125
      });
      this.$store.dispatch("filterElement", this.searchKeyword);
    },
    clearFilter() {
      this.searchKeyword = null;
      this.focusSuggest = 0;
    },
    // Suggest
    restructureData(x) {
      let type = {
        name: x.section
      };
      let item = {};
      if (x.section === "information") {
        Object.assign(type, { fieldName: "information" });
        Object.assign(item, {
          displayName: x.displayName,
          type: x.type
        });
      } else if (x.section === "state") {
        Object.assign(type, { fieldName: "states" });
        Object.assign(item, {
          name: x.name,
          color: x.color
        });
      } else if (x.section === "activity") {
        Object.assign(type, { fieldName: "activities" });
        Object.assign(item, {
          name: x.name,
          emoji: x.emoji
        });
      }
      return { type: type, item: item };
    },
    submitSuggest() {
      if (this.suggestions && this.suggestions.length) {
        this.editSuggest(this.suggestions[this.focusSuggest]);
      } else if (this.filterCard.length === 1 && this.searchResult) {
        this.openConsoleElement(this.filterCard[0], this.searchResult);
      }
    },
    selectSuggest(ind) {
      this.focusSuggest = ind;
      setTimeout(() => {
        this.$refs.searchKeyword.focus();
      }, 100);
      this.submitSuggest();
    },
    switchSuggest() {
      if (this.suggestions && this.suggestions.length) {
        if (this.focusSuggest == this.suggestions.length - 1)
          this.focusSuggest = 0;
        else this.focusSuggest += 1;
      }
    },
    editSuggest(x) {
      let data = this.restructureData(x);
      this.openConsoleElement(data.type, data.item);
    },
    setSuggesMouseHover(x, visible) {
      if (visible) this.$set(x, "mouseHover", true);
      else this.$delete(x, "mouseHover");
    },
    deleteModule() {
      this.$emit("deleteModule", this.vector);
    },
    toggleNotificationPanel() {
      this.showNotificationPanel = !this.showNotificationPanel;
      if (this.showNotificationPanel) {
        this.moduleAdvancedSettings = false;
      }
    },
    toggleModuleSettings() {
      this.moduleAdvancedSettings = !this.moduleAdvancedSettings;
      /* init connector */
      if (this.canvas && !this.canvas.connector) {
        this.$set(this.canvas, "connector", {});
      }
      /* hide notification panel */
      if (this.moduleAdvancedSettings) {
        this.showNotificationPanel = false;
      }
    },
    openModuleAutomationPanel() {
      this.$refs.ftsModuleAutomationPanelModal.show();
    },
    async openModuleHistory() {
      const res = await this.$refs.ftsModuleHistoryModal.show();
      if (res) {
        this.$store.dispatch("updateVectorCanvas", res);
      }
    },
    async openCanvasScriptModal() {
      if (this.isInternal) {
        try {
          const canvasStringify = JSON.stringify(this.canvas, null, '\t');
          const res = await this.$refs.ftsScriptEditorModal.show(canvasStringify, { title: "Module's Model" });
          if (res) {
            try {
              const canvas = JSON.parse(res);
              this.$store.dispatch("updateVectorCanvas", canvas);
            } catch(err) {
              this.$toasted.error(`Failed to update model. Error: ${err}`);
            }
          }
        } catch(err) {
          this.$toasted.error(`Failed to load model. Error: ${err}`);
        }
      }
    },
    async openSchemaScriptModal() {
      if (this.isInternal) {
        try {
          const res = await this.$refs.ftsScriptEditorModal.show('', { title: "Module Schema", mode: 'schema' });
          if (res) {
            try {
              // alert(res)
              // const canvas = JSON.parse(res);
              let test = await this.$store.dispatch("processSchema", res);
              this.$store.dispatch('loadAIVector', test);
            } catch(err) {
              this.$toasted.error(`Failed to generate model. Error: ${err}`);
            }
          }
        } catch(err) {
          this.$toasted.error(`Failed to generate model. Error: ${err}`);
        }
      }
    },
    scrollHandler() {
      if (this.vectorId) {
        const el = this.$refs.consoleSearchInput;

        if (
          el && 
          el.getBoundingClientRect() && 
          el.getBoundingClientRect().top === 55
        ) {
          el.classList.add("sticked");
        } else if (el && el.classList && el.classList.contains("sticked")) {
          el.classList.remove("sticked");
        }
      }
    }
  },
  computed: {
    isDevelopment() {
      return authorizationStore.getters.isDevelopment;
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    beta() {
      return authorizationStore.getters.isBeta || this.$store.getters.workspaceBeta;
    },
    /* Studio schedules (docs/scheduling-routine-automation-proposal.md §7.1): the adder is flag-gated;
       schedules that exist are always listed */
    studioScheduleEnabled() {
      return authorizationStore.getters.appStudioSchedule;
    },
    scheduleTemplates() {
      return (this.$store.getters.vectorTriggers || []).filter(isSchedule);
    },
    /* Module-level GNey instruction (top-level canvas ai_instruction). $set on write:
       the key usually doesn't exist on the canvas yet, and a plain assignment to a new
       key is not reactive in Vue 2 (the save would still carry it, but the counter and
       display would not update). */
    moduleAiInstruction: {
      get() {
        return (this.canvas && this.canvas.ai_instruction) || "";
      },
      set(value) {
        this.$set(this.canvas, "ai_instruction", value);
      },
    },
    cards() {
      return [
        {
          name: "standardActivity",
          fieldName: "standardActivities",
          displayName: "Basic",
          creatable: false,
          editable: false,
          draggable: false,
          authorization: true,
          automation: true,
          disabled: false,
          pageSize: 4,
          relyOn: []
        },
        {
          name: "information",
          fieldName: "information",
          displayName: "Information",
          creatable: this.isCapableOf(SYSTEM_CAPABILITY.AddInformation),
          editable: true,
          authorization: false,
          // Per-field access lock. Non-lockable fields (system/username/date/assignee/due/
          // title-referenced) are excluded - mirrors the server-side validation in
          // VectorSubmissionService.validateInformationAuthorizations.
          authorizationRule: (x) => this.isInformationLockable(x),
          automation: true,
          disabled: false,
          relyOn: []
        },
        {
          name: "activity",
          fieldName: "activities",
          displayName: "Activity",
          creatable: true,
          editable: true,
          authorization: true,
          automation: true,
          disabled: false,
          relyOn: []
        },
        {
          name: "state",
          fieldName: "states",
          displayName: "State",
          relyOn: [],
          creatable: true,
          editable: true,
          authorization: false,
          automation: true,
          disabled: !this.isCapableOf(SYSTEM_CAPABILITY.Workflow)
        },
        {
          name: "flow",
          fieldName: "flows",
          displayName: "Flow",
          creatable: true,
          editable: true,
          authorization: false,
          automation: false,
          disabled: !this.isCapableOf(SYSTEM_CAPABILITY.Workflow),
          relyOn: ["state", "activity"]
        },
        {
          name: "menu",
          fieldName: "menus",
          displayName: "Listing",
          creatable: true,
          editable: true,
          authorization: true,
          automation: false,
          disabled: !this.isCapableOf(SYSTEM_CAPABILITY.Query) || !this.isCapableOf(SYSTEM_CAPABILITY.Read),
          relyOn: ["information"]
        },
        {
          name: "document",
          fieldName: "documents",
          displayName: "Template",
          creatable: true,
          editable: true,
          authorization: true,
          automation: false,
          disabled: false,
          relyOn: ["information"]
        },
        {
          name: "action",
          fieldName: "actions",
          displayName: "Custom",
          creatable: true,
          editable: true,
          authorization: true,
          automation: false,
          disabled: false,
          relyOn: ["information"],
          beta: true
        },
        {
          name: "logicService",
          fieldName: "logicServices",
          displayName: "Logic",
          creatable: true,
          editable: true,
          authorization: false,
          automation: false,
          disabled: false,
          relyOn: ["information"],
          beta: true
        }
      ];
    },
    cardGroup() {
      return [
        {
          name: "group1",
          group: this.filterCard.filter(
            x =>
              (x.name == "standardActivity" || x.name == "menu") &&
              (!x.beta || (this.beta && x.beta))
          )
        },
        {
          name: "group2",
          group: this.filterCard.filter(
            x =>
              (x.name == "information" ||
                x.name == "action" ||
                x.name == "document") &&
              (!x.beta || (this.beta && x.beta))
          )
        },
        {
          name: "group3",
          group: this.filterCard.filter(
            x =>
              (x.name == "activity" ||
                x.name == "state" ||
                x.name == "flow" ||
                x.name == "logicService") &&
              (!x.beta || (this.beta && x.beta))
          )
        }
      ];
    },
    information() {
      return this.$store.getters.canvasInformation;
    },
    workspace() {
      return this.$store.state.workspace || {};
    },
    workspaceModules() {
      return this.$store.getters.workspaceModules;
    },
    searchKeywordToLower() {
      return this.searchKeyword && this.searchKeyword.toLowerCase();
    },
    suggestions() {
      return this.searchKeyword ? this.$store.state.suggestions : [];
    },
    filterCard() {
      let cards = this.cards;
      if (!!this.searchKeyword) {
        return this.cards.filter(
          x =>
            x.name == "vector" ||
            x.displayName.toLowerCase().indexOf(this.searchKeywordToLower) > -1 ||
            this.checkContent(x.fieldName, x.name)
        );
      }
      return cards;
    },
    searchResult() {
      let results = [];

      try {
        this.cards.forEach(x => {
          results = results.concat(
            this.canvas[x.fieldName].filter(
              c =>
                (c.displayName &&
                  c.displayName.toLowerCase().indexOf(this.searchKeywordToLower) >
                    -1) ||
                (c.name &&
                  c.name.toLowerCase().indexOf(this.searchKeywordToLower) > -1)
            )
          );
        });
      } catch (err) {
        console.warn(err);
      }

      if (results.length === 1 && this.filterCard.length === 1)
        return results[0];
    },
    moduleWorkspace: {
      get() {
        if (this.vector.ownerWorkspaceId) {
          let module = { id: this.vector.ownerWorkspaceId };
          if (this.workspace.id === this.vector.ownerWorkspaceId)
            module.name = this.$store.state.workspace.name;
          else module.name = this.vector.ownerWorkspace;

          return module;
        }
        return null;
      },
      set(val) {
        if (val) {
          this.vector.ownerWorkspaceId = val.id;
          this.vector.ownerWorkspace = val.name;
        } else {
          this.vector.ownerWorkspaceId = null;
          this.vector.ownerWorkspace = null;
        }
      }
    },
    consoleElementDisableList() {
      let list = [];
      if (!this.isCapableOf(SYSTEM_CAPABILITY.Write)) {
        list = list.concat([
          { id: 'create', type: 'standardActivity'},
          { id: 'edit', type: 'standardActivity'},
          { id: 'changeStatus', type: 'standardActivity'},
          { id: 'duplicate', type: 'standardActivity'},
          { id: 'assign', type: 'standardActivity'},
          { id: 'import', type: 'standardActivity'}
        ]);
      }
      if (!this.isCapableOf(SYSTEM_CAPABILITY.Delete)) {
        list = list.concat([
          { id: 'delete', type: 'standardActivity'}
        ]);
      }
      if (!this.isCapableOf(SYSTEM_CAPABILITY.Read)) {
        list = list.concat([
          { id: 'view', type: 'standardActivity'},
          { id: 'quickView', type: 'standardActivity'}
        ]);
      }
      if (
        !this.isCapableOf(SYSTEM_CAPABILITY.Write) &&
        !this.isCapableOf(SYSTEM_CAPABILITY.Read)
      ) {
        list = list.concat([
          { id: 'createEditView', type: 'standardActivity'}
        ]);
      }
      return list;
    },
    informationSystemDefault() {
      const res = [...canvasDesign.informationSystemDefault];
      return {
        information: this.vector && this.vector.isUserCollection ?
          res : res.filter(x => !x.userCollection)
      }
    },
    connectorForm() {
      return this.canvas.connector && this.canvas.connector.type && connectorCommons.form(this.canvas.connector.type);
    },
    mutableCanvas: {
      get() {
        return this.canvas;
      },
      set(val) {
        this.$store.dispatch("updateVectorCanvas", val);
      }
    }
  },
  watch: {
    vectorProps: function() {
      if (
        (this.vectorId && this.vector && !this.vector.id) ||
        (this.vector && this.vector.id != this.vectorId)||
        (this.vector && this.vectorProps && this.vector.emoji != this.vectorProps.emoji)
      ) {
        this.vector = this.vectorProps;
        this.reset();

        this.$nextTick(() => {
          if (this.$refs.searchKeyword && this.vectorId)
            this.$refs.searchKeyword.focus();
          else if (this.$refs.moduleName) this.$refs.moduleName.$el.focus();
        });
      }
    },
    canvasProps: function() {
      this.canvas = this.canvasProps;
    },
    canvas: async function(val) {
      if (val == null) return;

      Vue.set(val, "documents", val.documents);
      Vue.set(val, "actions", val.actions);
      for (var card of this.cards) {
        if (!val[card.fieldName]) {
          Vue.set(val, card.fieldName, []);
        }
      }

      let vm = this;

      this.$store.dispatch("loadCanvas", {
        canvas: val,
        baseServer: this.baseServer,
        token: this.token,
        vector: this.vectorId
      });

      if (
        vm.$store.state.workspace &&
        !vm.workspaceOptions.some(x => x.id == vm.$store.state.workspace.id)
      ) {
        vm.workspaceOptions.push(vm.$store.state.workspace);
      }

      let path = this.$route.path;
      let params = path.split("/");
      if (
        (params.length && params[1] == "uc") ||
        (params.length && params[1] == "ucd")
      ) {
        let card = this.sections[1].cards.find(x => x.fieldName == params[2]);
        if (card) {
          let item = this.canvas[card.fieldName].find(x => x.id == params[3]);

          if (params.length && params[1] == "uc") {
            // to:do open modal via route path
          } else {
            window.eventBus.$emit("workDesignData", {
              item: item
            });
          }
        }
      }
    }
  },
  mounted() {
    // eventBus
    window.eventBus.$on("ftsConsoleOnScroll", this.scrollHandler);
    // GNey document drafts (D16): the studio panel hands a drafted htmljs bundle over the
    // bus; this console opens its own htmljs editor on it — live preview as the review
    // surface, the modal's normal save path as the commit.
    window.eventBus.$on("gney:openHtmlJsDraft", this.openGneyHtmlJsDraft);
    // GNey automation review (D19): after a checklist apply lands a trigger in the draft,
    // the card's Review button opens it in the automation designer by id.
    window.eventBus.$on("gney:openAutomation", this.openGneyAutomation);
    // scroll to
    this.$emit("scrollTo", { x: 0, y: 0 });
    // set canvas
    this.canvas = this.canvasProps;
    // set vector
    if (this.vectorId) {
      this.vector = this.vectorProps;
    }
    // load canvas
    this.$store.dispatch("loadCanvas", {
      canvas: this.canvas,
      baseServer: this.baseServer,
      token: this.token,
      vector: this.vectorId
    });
    // select workspace
    if (this.workspaceId != null) {
      this.$store.dispatch("selectWorkspace", { id: this.workspaceId });
    }
    /**
     * route query
     * t - design type
     * tid - design ID
     */
    const designName = this.$route.query.t;
    const designId = this.$route.query.tid;
    const getCanvasItemByTypeAndId = (type, id) => {
      if (type === "information") {
        return canvasDesign.findInformation(this.canvas[type], id);
      } else {
        return this.canvas[type].find(x => x.id == id);
      }
    };

    if (designId) {
      try {
        if (["automation", "notification", "script"].includes(designName)) {
          const selectedTrigger = getCanvasItemByTypeAndId("triggers", designId);

          if (selectedTrigger) {
            this.showAutomationDesignModal(selectedTrigger);
          }
        } else if (
          ["standardActivity", "activity", "menu", "information", "state"].includes(designName)
        ) {
          const type = this.cards.find(x => x.name == designName);
          let selectedItem = getCanvasItemByTypeAndId(type.fieldName, designId);

          if (selectedItem) {
            if (designName == "standardActivity") {
              if (
                ["create", "edit", "quickView", "view"].includes(designId) && 
                !selectedItem.design
              ) {
                selectedItem = null;
              }

              if (!selectedItem) {
                if (designId === "quickView") {
                  selectedItem = getCanvasItemByTypeAndId(type.fieldName, "view");
                  commons.addDesignQueryParams(this.$router, this.$route, [
                    { name: "tid", value: "view" }
                  ], true);

                  if (!selectedItem.design) {
                    selectedItem = null;
                  }
                } else if (designId === "view") {
                  selectedItem = getCanvasItemByTypeAndId(type.fieldName, "quickView");
                  commons.addDesignQueryParams(this.$router, this.$route, [
                    { name: "tid", value: "quickView" }
                  ], true);

                  if (!selectedItem.design) {
                    selectedItem = null;
                  }
                }

                if (!selectedItem) {
                  selectedItem = getCanvasItemByTypeAndId(type.fieldName, "createEditView");
                  commons.addDesignQueryParams(this.$router, this.$route, [
                    { name: "tid", value: "createEditView" }
                  ], true);
                }
              }
            }

            this.openConsoleElement(type, selectedItem);
            return;
          } else {
            this.$toasted.error(`"${designId}" was not found in ${designName}. It may have been deleted or is no longer available.`);
            commons.removeDesignQueryParams(this.$router, this.$route, ["t", "tid"], true);
          }
        }
      } catch(error) {
        console.warn("FTSConsole", error);
      }
    }
    // autofocus search input
    this.$nextTick(() => {
      if (this.$refs.searchKeyword && this.vectorId) {
        this.$refs.searchKeyword.focus();
      } else if (this.$refs.moduleName) {
        this.$refs.moduleName.$el.focus();
      }
    });
  },
  beforeDestroy() {
    // event bus
    window.eventBus.$off("ftsConsoleOnScroll", this.scrollHandler);
    window.eventBus.$off("gney:openHtmlJsDraft", this.openGneyHtmlJsDraft);
    window.eventBus.$off("gney:openAutomation", this.openGneyAutomation);
    // reset store
    this.$store.dispatch('resetVector');
  }
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;

/* the page design pinned above the views (R13, §5.7) */
.console-card-pinned {
  padding: 0 0 0.25rem;
  margin-bottom: 0.25rem;
  border-bottom: 1px dashed var(--fts-border);
}
.console-card-pinned__row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.5rem;
  border-radius: 0.25rem;
  cursor: pointer;
}
.console-card-pinned__row:hover {
  background: var(--fts-gray-100);
}
.console-card-pinned__name {
  font-weight: 500;
}
.console-card-pinned__tag {
  font-size: 0.625rem;
  line-height: 1.4;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.0625rem 0.375rem;
  border-radius: 0.25rem;
  background: var(--fts-gray-100);
  color: var(--inistate-text-secondary-color);
}
.console-card-pinned__hint {
  margin-left: auto;
  font-size: 0.75rem;
  text-align: right;
  color: var(--inistate-text-secondary-color);
}

.fts-console {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  padding-bottom: 5rem;
  color: var(--fts-text);
  &.mh {
    min-height: calc(100vh + 560px);
  }
  /* section */
  .console-section {
    display: flex;
    .console-section-wrapper {
      flex: 1 1 33.333333%;
      max-width: 33.333333%;
      display: flex;
      flex-direction: column;
      .console-card-header {
        padding-left: 1.375rem;
      }
    }
  }
  /* card */
  .console-card {
    margin: 0.5rem;
    border-radius: 0.5rem;
    background: var(--fts-card-background);
    box-shadow: var(--fts-elevation-1);
    &.inactive {
      filter: grayscale(0.5);
      .console-card-header {
        border-bottom-left-radius: inherit;
        border-bottom-right-radius: inherit;
        &:after {
          content: "";
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          border-color: transparent;
          border-radius: inherit;
          background-color: var(--fts-disabled);
          cursor: not-allowed;
        }
      }
    }
    .console-card-header {
      position: relative;
      padding: 0.5rem 1rem;
      border-top-left-radius: inherit;
      border-top-right-radius: inherit;
      z-index: 1;
      .console-card-title-wrapper {
        display: flex;
        align-items: center;
        .console-card-title {
          flex: 1;
          font-weight: 500;
          font-size: 0.875rem;
          line-height: 1.75rem;
          color: var(--fts-text);
        }
        .console-card-action-create {
          margin-left: 1rem;
        }
      }
      .console-card-error {
        margin-bottom: 0.5rem;
        font-size: 0.875rem;
        line-height: 1.25rem;
        color: var(--fts-danger);
      }
    }
    .console-card-body {
      .console-card-content {
        display: flex;
        align-items: center;
        padding: 0 1rem 0.75rem;
      }
      .console-card-element {
        max-height: 600px;
        min-height: 2rem;
        overflow: auto;
        border-top: 1px solid var(--fts-border);
      }
      .console-card-body-divider {
        border-top: 1px solid var(--fts-border);
      }
      .console-card-disabled {
        padding: 1rem;
        font-size: 0.875rem;
        line-height: 1.25rem;
        color: var(--fts-default);
      }
      .console-card-body__footer {
        display: flex;
        align-items: center;
        padding: 0.75rem 1rem;
        &:not(:first-child) {
          border-top: 1px solid var(--fts-border);
        }
      }
      .console-card-content-actions {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        margin-left: 1rem;
        .console-card-event-btn {
          &:not(:first-child) {
            margin-left: 0.5rem;
          }
          &:not(:last-child) {
            margin-right: 0.5rem;
          }
        }
      }
      .console-card-content > .console-card-content-actions {
        gap: 0.5rem;
        .console-card-action-group {
          display: flex;
          align-items: center;
        }
      }
    }
    /* automation */
    .console-card-automation-container {
      @include flex-gap($display: flex, $gap: 0.375rem);
    }
    .console-card-automation-container + .console-card-audit {
      margin-top: 0.5rem;
    }
    /* audit */
    .console-card-audit {
      display: flex;
      align-items: center;
      font-size: 0.75rem;
      line-height: 1rem;
      .console-card-audit-by {
        margin-right: 0.375rem;
        color: var(--fts-default);
        white-space: nowrap;
      }
      .console-card-audit-date {
        margin-left: 0.75rem;
        color: var(--fts-default);
        white-space: nowrap;
      }
    }
  }
  /* module form */
  .console-card-module {
    .console-card-header {
      display: flex;
      padding-top: 0.75rem;
    }
    .console-card-header-wrapper {
      min-width: 0;
      flex: 1;
    }
    .console-card-module-info {
      display: flex;
      align-items: center;
      margin-bottom: 0.25rem;
      .console-card-module-name {
        width: auto !important;
        padding-left: 0.375rem;
        padding-right: 0.375rem;
        font-size: 1rem;
        font-weight: 500;
        color: var(--fts-text);
      }
      .console-card-module-tag {
        display: inline-flex;
        align-items: center;
        padding: 0.125rem 0.5rem;
        font-size: 0.75rem;
        line-height: 1rem;
        border-radius: 0.25rem;
        background-color: var(--drawer-tag-background, var(--drawer-secondary-background));
        color: var(--drawer-secondary);
      }
    }
    .console-card-module-description {
      margin-bottom: 0.25rem;
      font-size: 0.875rem;
      line-height: 1.25rem;
      color: var(--fts-text);
    }
    .console-card-toggle {
      margin-left: 1rem;
      > *:not(:first-child) {
        margin-left: 0.75rem;
      }
      .console-card-toggle-label {
        display: flex;
        align-items: center;
        font-weight: 500;
        font-size: 0.75rem;
        color: var(--fts-form-label);
        .label-helper {
          margin-left: 0.375rem;
          font-size: 0.875rem;
          cursor: default;
        }
      }
    }
    .console-card-module-form {
      padding: 0.75rem;
      .form-divider {
        margin-top: 1rem;
      }
      .console-form-legend {
        margin-bottom: 1.125rem;
        padding-bottom: 0.5rem;
        border-bottom: 1px solid var(--fts-border);
        font-size: 1.125rem;
        line-height: 1.5;
        color: var(--fts-form-label)
      }

      /* teammate instructions (module-level ai_instruction) */
      .console-teammate-instruction-input {
        width: 100%;
        resize: vertical;
        padding: 8px 12px;
        border: 1px solid var(--fts-border);
        border-radius: 6px;
        font-family: inherit;
        font-size: 13px;
        line-height: 1.5;
        background: transparent;
        color: inherit;

        &:focus {
          outline: none;
          border-color: #2b9ec6;
        }
      }

      .console-teammate-instruction-meta {
        margin-top: 4px;
        font-size: 12px;
        text-align: right;
        color: var(--fts-form-label);
      }
    }
    .console-card-create-action {
      padding: 1rem;
    }
  }
  /* search */
  .console-search {
    position: sticky;
    top: 55px;
    display: flex;
    align-items: center;
    padding: 0.5rem 0.375rem;
    transition: all 0.3s ease-in-out;
    z-index: 50;
    .fts-console-search-input {
      flex: 1 0 768px;
      max-width: 768px;
    }
    &.sticked {
      @include theme() {
        @include backdrop-background(
          $color: theme-get('fts-background'), 
          $alpha: 0.72, 
          $blur: 20px
        );
      }
    }
  }
  /* suggestion */
  .console-suggest {
    .console-suggest-content {
      padding: 0.875rem;
      .console-suggest-title {
        margin-bottom: 0.275rem;
        font-weight: 500;
        font-size: 1.15rem;
        color: var(--fts-default);
      }
      .console-suggest-desc {
        font-size: 0.875rem;
        color: var(--fts-default);
      }
      .console-suggest-results {
        display: flex;
        flex-wrap: wrap;
        margin: -0.5rem;
        padding-top: 1rem;
        .console-suggest-result {
          max-width: 20%;
          flex: 1 1 20%;
          padding: 0.5rem;
          .console-suggest-card {
            padding: 0.75rem;
            border: 1px solid var(--fts-border);
            border-radius: 0.5rem;
            background: var(--fts-card-background);
            box-shadow: var(--fts-elevation-1);
            transition: border-color 0.15s ease, box-shadow 0.15s ease;
            &.focused {
              @include theme() {
                box-shadow: 0 2px 6px 0
                  rgba($color: theme-get("fts-primary"), $alpha: 0.18);
              }
              border-color: var(--fts-primary);
            }
            .console-suggest-card-header {
              min-width: 0;
              display: flex;
              align-items: center;
              font-size: 0.875rem;
              color: var(--fts-gray-400);
              .console-suggest-card-check {
                margin-right: 0.575rem;
              }
              .console-suggest-card-title {
                @include text-overflow-hidden;
                text-transform: capitalize;
              }
            }
            .console-suggest-card-body {
              position: relative;
              min-height: 28px;
              margin-top: 0.575rem;
              .console-suggest-element {
                display: flex;
                align-items: center;
                font-size: 1.275rem;
                .console-suggest-element-icon {
                  margin-right: 0.575rem;
                }
                .console-suggest-element-name {
                  @include text-overflow-hidden;
                  color: var(--fts-text);
                }
              }
              .console-suggest-state {
                > div {
                  font-size: 1rem;
                }
              }
              .console-suggest-card-action {
                @include position(
                  $top: 50%,
                  $right: -0.175rem,
                  $transform: translateY(-50%)
                );
              }
            }
          }
        }
      }
    }
  }
  /* no result */
  .console-no-result {
    @include flex-center-middle;
    flex-direction: column;
    min-height: 150px;
    .no-result-label {
      font-size: 1.15rem;
      font-weight: 600;
    }
    .no-result-desc {
      margin-top: 0.575rem;
      font-size: 0.875rem;
    }
  }
  /* import */
  .console-import {
    display: flex;
    align-items: center;
    .console-import__auth {
      margin-left: 0.75rem;
    }
  }
}
</style>
